import { FC, useEffect, useMemo } from 'react';
import {
	Autocomplete,
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	FormControl,
	FormControlLabel,
	Grid,
	InputLabel,
	MenuItem,
	Select,
	Switch,
	TextField,
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { EEditParamsDialogProps } from 'components/EMManagement/EMManagementId/EasParamsBox/types';
import { ButtonCircularProgress } from 'ui/ButtonCircularProgress';
import {
	resetSavePpwrEmSettingsSuccess,
	updatePpwrEmSettingsRequest,
} from 'redux/reducers/ppwr/reducer';
import {
	getPpwrEmSettingsDetails,
	getPpwrEmSettingsSaveLoading,
	getPpwrEmSettingsSaveSuccess,
	getPpwrFeeOptions,
	getPpwrFeeOptionsLoading,
} from 'redux/reducers/ppwr/selectors';
import { TPpwrEmSettings, TPpwrFee } from 'redux/reducers/ppwr/types';

/**
 * Formik stores a `type="number"` input as a number once it is edited, and as `''` when it is
 * cleared, so number fields hold either. An empty one is sent as `null`.
 */
type TNumberFieldValue = number | string;

type TPpwrSettingsFormValues = {
	enabled: boolean;
	subscriptionPlanId: number | '';
	avgSalesMonthly: TNumberFieldValue;
	avgParcelItems: TNumberFieldValue;
	/** `''` stands for "not set", the API field is a nullable boolean. */
	b2bSales: 'yes' | 'no' | '';
	avgB2bSalesMonthly: TNumberFieldValue;
	b2cCountInB2b: TNumberFieldValue;
	wholesaleMethod: string;
	fulfilmentMethod: string;
	typicalB2cOrder: string;
};

const NUMBER_FIELDS: { name: keyof TPpwrSettingsFormValues; label: string }[] = [
	{ name: 'avgSalesMonthly', label: 'Avg Sales Monthly' },
	{ name: 'avgParcelItems', label: 'Avg Parcel Items' },
	{ name: 'avgB2bSalesMonthly', label: 'Avg B2B Sales Monthly' },
	{ name: 'b2cCountInB2b', label: 'B2C Count in B2B' },
];

const TEXT_FIELDS: { name: keyof TPpwrSettingsFormValues; label: string }[] = [
	{ name: 'wholesaleMethod', label: 'Wholesale Method' },
	{ name: 'fulfilmentMethod', label: 'Fulfilment Method' },
	{ name: 'typicalB2cOrder', label: 'Typical B2C Order' },
];

/** An empty input means "not set": Yup would otherwise cast `''` to NaN and fail the type check. */
const optionalNumber = Yup.number()
	.transform((value, originalValue) =>
		typeof originalValue === 'string' && originalValue.trim() === '' ? undefined : value,
	)
	.typeError('Must be a number')
	.min(0, 'Must be 0 or more')
	.notRequired();

const validationSchema = Yup.object().shape({
	subscriptionPlanId: Yup.number().required('Required'),
	avgSalesMonthly: optionalNumber,
	avgParcelItems: optionalNumber,
	avgB2bSalesMonthly: optionalNumber,
	b2cCountInB2b: optionalNumber,
});

const toFormValues = (settings: TPpwrEmSettings | null): TPpwrSettingsFormValues => {
	const numberText = (value: number | null | undefined) =>
		value === null || value === undefined ? '' : String(value);
	let b2bSales: TPpwrSettingsFormValues['b2bSales'] = '';
	if (settings?.b2bSales === true) b2bSales = 'yes';
	if (settings?.b2bSales === false) b2bSales = 'no';
	return {
		enabled: settings?.enabled ?? false,
		subscriptionPlanId: settings?.subscriptionPlanId ?? '',
		avgSalesMonthly: numberText(settings?.avgSalesMonthly),
		avgParcelItems: numberText(settings?.avgParcelItems),
		b2bSales,
		avgB2bSalesMonthly: numberText(settings?.avgB2bSalesMonthly),
		b2cCountInB2b: numberText(settings?.b2cCountInB2b),
		wholesaleMethod: settings?.wholesaleMethod ?? '',
		fulfilmentMethod: settings?.fulfilmentMethod ?? '',
		typicalB2cOrder: settings?.typicalB2cOrder ?? '',
	};
};

const toNumberOrNull = (value: TNumberFieldValue) =>
	String(value).trim() === '' ? null : Number(value);
const toTextOrNull = (value: string) => (value.trim() === '' ? null : value.trim());

const getFeeLabel = (fee: TPpwrFee) =>
	`${fee.planName ?? `Plan #${fee.id}`} (monthly ${fee.monthlyFee}, per order ${fee.perOrderFee})`;

/** Edit dialog of the EPR/PPWR Settings box, saves through PUT /api/ppwr/em_settings/{emId}. */
export const PpwrSettingsEditDialog: FC<EEditParamsDialogProps> = ({ open, onClose }) => {
	const dispatch = useDispatch();
	const settings = useSelector(getPpwrEmSettingsDetails);
	const saveLoading = useSelector(getPpwrEmSettingsSaveLoading);
	const saveSuccess = useSelector(getPpwrEmSettingsSaveSuccess);
	const feeOptions = useSelector(getPpwrFeeOptions);
	const feeOptionsLoading = useSelector(getPpwrFeeOptionsLoading);

	const initialValues = useMemo(() => toFormValues(settings), [settings]);

	const formik = useFormik<TPpwrSettingsFormValues>({
		initialValues,
		enableReinitialize: true,
		validationSchema,
		onSubmit: (values) => {
			if (!settings) return;
			dispatch(
				updatePpwrEmSettingsRequest({
					emId: settings.emId,
					body: {
						enabled: values.enabled,
						subscriptionPlanId: Number(values.subscriptionPlanId),
						avgSalesMonthly: toNumberOrNull(values.avgSalesMonthly),
						avgParcelItems: toNumberOrNull(values.avgParcelItems),
						b2bSales: values.b2bSales === '' ? null : values.b2bSales === 'yes',
						avgB2bSalesMonthly: toNumberOrNull(values.avgB2bSalesMonthly),
						b2cCountInB2b: toNumberOrNull(values.b2cCountInB2b),
						wholesaleMethod: toTextOrNull(values.wholesaleMethod),
						fulfilmentMethod: toTextOrNull(values.fulfilmentMethod),
						typicalB2cOrder: toTextOrNull(values.typicalB2cOrder),
					},
				}),
			);
		},
	});

	const { resetForm } = formik;

	const handleClose = () => {
		resetForm();
		onClose();
	};

	useEffect(() => {
		if (!open || !saveSuccess) return;
		dispatch(resetSavePpwrEmSettingsSuccess());
		onClose();
	}, [dispatch, open, saveSuccess, onClose]);

	const selectedFee = useMemo(
		() =>
			feeOptions?.find((fee) => String(fee.id) === String(formik.values.subscriptionPlanId)) ??
			null,
		[feeOptions, formik.values.subscriptionPlanId],
	);

	const fieldError = (name: keyof TPpwrSettingsFormValues) =>
		formik.touched[name] && formik.errors[name] ? String(formik.errors[name]) : undefined;

	return (
		<Dialog open={open} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: '12px' } }}>
			<DialogTitle color="primary">Edit EPR/PPWR Settings</DialogTitle>
			<form onSubmit={formik.handleSubmit}>
				<DialogContent dividers>
					<Grid container spacing={2}>
						<Grid item xs={12} sm={4}>
							<FormControlLabel
								control={
									<Switch
										name="enabled"
										checked={formik.values.enabled}
										onChange={formik.handleChange}
									/>
								}
								label="Enabled"
							/>
						</Grid>
						<Grid item xs={12} sm={8}>
							<Autocomplete
								size="small"
								fullWidth
								options={feeOptions ?? []}
								loading={feeOptionsLoading}
								value={selectedFee}
								onChange={(_event, value) => {
									formik.setFieldValue('subscriptionPlanId', value?.id ?? '').catch(() => {});
								}}
								isOptionEqualToValue={(option, value) => option.id === value.id}
								getOptionLabel={getFeeLabel}
								renderInput={(params) => (
									<TextField
										{...params}
										name="subscriptionPlanId"
										label="Subscription"
										required
										onBlur={() => {
											formik.setFieldTouched('subscriptionPlanId', true).catch(() => {});
										}}
										error={!!fieldError('subscriptionPlanId')}
										helperText={fieldError('subscriptionPlanId')}
									/>
								)}
							/>
						</Grid>

						{NUMBER_FIELDS.map((field) => (
							<Grid item xs={12} sm={6} key={field.name}>
								<TextField
									fullWidth
									size="small"
									type="number"
									label={field.label}
									name={field.name}
									value={formik.values[field.name]}
									onChange={formik.handleChange}
									onBlur={formik.handleBlur}
									error={!!fieldError(field.name)}
									helperText={fieldError(field.name)}
								/>
							</Grid>
						))}

						<Grid item xs={12} sm={6}>
							<FormControl fullWidth size="small">
								<InputLabel>B2B Sales</InputLabel>
								<Select
									name="b2bSales"
									value={formik.values.b2bSales}
									label="B2B Sales"
									onChange={formik.handleChange}
								>
									<MenuItem value="">Not set</MenuItem>
									<MenuItem value="yes">Yes</MenuItem>
									<MenuItem value="no">No</MenuItem>
								</Select>
							</FormControl>
						</Grid>

						{TEXT_FIELDS.map((field) => (
							<Grid item xs={12} sm={6} key={field.name}>
								<TextField
									fullWidth
									size="small"
									label={field.label}
									name={field.name}
									value={formik.values[field.name]}
									onChange={formik.handleChange}
								/>
							</Grid>
						))}
					</Grid>
				</DialogContent>
				<DialogActions sx={{ p: 2 }}>
					<Button onClick={handleClose} color="inherit" disabled={saveLoading}>
						Cancel
					</Button>
					<Button
						type="submit"
						variant="contained"
						disabled={!settings || saveLoading || !formik.dirty}
					>
						{saveLoading ? <ButtonCircularProgress /> : 'Save'}
					</Button>
				</DialogActions>
			</form>
		</Dialog>
	);
};
